import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import adler32 from 'adler-32';

import { withTheme } from '../core/theming';
import Div from './Div';
import Icon from './Icon';
import Text from './Text';

const findDropdownDir = (e) => {
  switch (e) {
    case 'topright':
      return 'transform-origin: 100% 100%; bottom: calc(100% + 2px); right: 0;';
    case 'topleft':
      return 'transform-origin: 0 100%; bottom: calc(100% + 2px); left: 0;';
    case 'bottomright':
      return 'transform-origin: 50% 0; top: calc(100% + 2px); right: 0;';
    case 'righttop':
      return 'transform-origin: 0 0; top: 0; left: calc(100% + 2px);';
    case 'lefttop':
      return 'transform-origin: 0 0; top: 0; right: calc(100% + 2px);';
    default:
      return 'transform-origin: 50% 0; top: calc(100% + 2px); left: 0;';
  }
};

const BaseDropdown = styled(Div)`
  > div:last-child {
    position: absolute;
    z-index: 10;
    ${(props) => findDropdownDir(props.direction)}
    transition: all 0.2s ease-in-out;
    opacity: ${(props) => (props.isOpen ? '1' : '0')};
    transform: scaleY(${(props) => (props.isOpen ? '1' : '0')});
  }

  ${(props) => props.targetHover && `:hover { border-color: ${props.theme.colors[props.focusBorderColor]}}`}
  ${(props) => props.targetHover && ':hover > div:last-child{ opacity: 1; transform: scaleY(1);}'}
`;

class Dropdown extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedValue: null,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleMenuClick = this.handleMenuClick.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { resetValue = null } = this.props;
    if (resetValue && prevProps.resetValue !== resetValue) {
      this.setState({ selectedValue: null });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick, false);
  }

  handleClick() {
    const { isOpen, onClick } = this.props;

    if (!isOpen) {
      document.addEventListener('mousedown', this.handleOutsideClick, false);
    } else {
      document.removeEventListener('mousedown', this.handleOutsideClick, false);
    }
    onClick();
  }

  handleMenuClick(index) {
    const { menu, onSelect } = this.props;
    this.setState({
      selectedValue: menu[index],
    });
    onSelect(index);
  }

  handleOutsideClick(e) {
    // eslint-disable-next-line react/no-find-dom-node
    if (ReactDOM.findDOMNode(this).contains(e.target)) {
      return;
    }

    this.handleClick();
  }

  /* eslint-disable no-nested-ternary */
  /* eslint-disable react/prop-types */

  render() {
    const {
      menu,
      menuOptions,
      menuItemOptions,
      isOpen,
      onClick,
      targetHover,
      prefix,
      openSuffix,
      closeSuffix,
      defaultValue,
      isLoading,
      borderColor,
      focusBorderColor,
      bg,
      focusBg,
      ...rest
    } = this.props;
    const { selectedValue } = this.state;
    return (
      <BaseDropdown
        ref={(node) => {
          this.node = node;
        }}
        onClick={!isLoading ? (targetHover ? null : this.handleClick) : () => {}}
        targetHover={targetHover}
        pos="relative"
        isOpen={isOpen}
        borderColor={isOpen ? focusBorderColor : borderColor}
        bg={isOpen ? focusBg : bg}
        {...rest}
      >
        {prefix}
        {selectedValue || defaultValue}
        {isLoading ? <Icon name="Loading" color="gray600" size="18px" /> : isOpen ? openSuffix : closeSuffix}
        <Div {...menuOptions}>
          {menu.map((item, index) => (
            <Div key={adler32.str(`${item}`)} {...menuItemOptions} onClick={() => this.handleMenuClick(index)}>
              {item}
            </Div>
          ))}
        </Div>
      </BaseDropdown>
    );
  }

  /* eslint-enable react/prop-types */
  /* eslint-enable no-nested-ternary */
}

/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */

Dropdown.propTypes = {
  resetValue: PropTypes.bool,
  defaultValue: PropTypes.node,
  // Must be wrapped around <DropdownMenu>
  menu: PropTypes.node,
  // Supports menu options of Div
  menuOptions: PropTypes.object,
  menuItemOptions: PropTypes.object,
  isOpen: PropTypes.bool,
  // change the target from click to hover
  targetHover: PropTypes.bool,
  direction: PropTypes.oneOf(['bottomleft', 'bottomright', 'topright', 'topleft', 'righttop', 'lefttop']),
  isLoading: PropTypes.bool,
  // Component typically icon placed before the component
  prefix: PropTypes.node,
  // Component (icon) placed at the end of the component when is open
  openSuffix: PropTypes.node,
  // Component (icon) placed at the end of the component when is closed
  closeSuffix: PropTypes.node,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
};

/* eslint-enable react/forbid-prop-types */
/* eslint-enable react/require-default-props */

/* eslint-disable react/default-props-match-prop-types */

Dropdown.defaultProps = {
  tag: 'div',
  d: 'flex',
  align: 'center',
  textColor: 'medium',
  textWeight: '500',
  justify: 'space-between',
  p: { x: '12px' },
  h: '38.4px',
  textSize: 'body',
  rounded: 'md',
  border: '1px solid',
  borderColor: 'tabDisabled',
  w: '100%',
  bg: 'white',
  focusBg: 'tabRest',
  focusBorderColor: 'tabSelected',
  cursor: 'pointer',
  onClick: () => {},
  onSelect: () => null,
  resetValue: false,
  defaultValue: <Text>Please select a value</Text>,
  openSuffix: <Icon name="UpArrow" size="12px" m={{ l: '8px' }} color="medium" p="2.08px" />,
  closeSuffix: <Icon name="DownArrow" size="12px" m={{ l: '8px' }} color="medium" p="2.08px" />,
  menuOptions: {
    pos: 'absolute',
    shadow: '4',
    minW: '100%',
    rounded: 'md',
    bg: 'white',
  },
  menuItemOptions: {
    p: { x: '16px', y: '8px' },
    hoverBg: 'tabRest',
  },
};

/* eslint-enable react/default-props-match-prop-types */

export default withTheme(Dropdown);
